<script>
	// NPM Modules
	import Link from 'svelte-navaid/Link.svelte';
	// Stores
	import LinkGroupStore from '@/Stores/LinkGroupStore.js';
	import SportStore from '@/Stores/Sport/SportStore.js';
	// Components
	import Loading from '@/Components/Loading.svelte';
	// Partials
	import PromotionsLink from '@/Partials/LinkGroup/_PromotionsLink.svelte';
	import CustomisedMenusLink from '@/Partials/LinkGroup/_CustomisedMenusLink.svelte';
	import { notify } from '@/Services/Notify';
	import Helmet from '@/Components/Helmet.svelte';
	import { LinkGroupCategories, LinkGroupCategoryNames } from '@/Constants/LinkGroups.js';

	export let slug;
	export let group;
	export let category = LinkGroupCategoryNames[LinkGroupCategories.General].toLowerCase();

	const LinkGroupMap = {
		promotions: {
			title: 'Promotions',
			id: 1,
			component: PromotionsLink,
			showCategories: true
		},
		customised_menus: {
			title: 'Customised Menus',
			id: 2,
			component: CustomisedMenusLink,
			showCategories: false
		}
	};

	let ghost = null;
	let grabbed = false;
	let lastTarget;
	let mouseY = 0; // pointer y coordinate within client
	let offsetY = 0; // y distance from top of grabbed element to pointer
	let layerY = 0; // distance from top of list to top of client
	let mouseYMax = 0; // distance from top of list to top of client
	let mouseYMin = 0; // distance from top of list to top of client
	let hoveredLink;

	let grabbedLink = null;
	$: linkGroupTypeId = slug && LinkGroupMap[slug.toLowerCase()].id;
	$: linkCategoryId =
		(category || !category) &&
		getLinkCategoryId(category ?? LinkGroupCategoryNames[LinkGroupCategories.General].toLowerCase());
	$: loading =
		linkGroupTypeId &&
		(linkCategoryId || !linkCategoryId) &&
		LinkGroupStore.getLinkGroups(linkGroupTypeId, linkCategoryId);
	$: linkGroup =
		$LinkGroupStore &&
		((group && $LinkGroupStore.firstWhere('id', group)) ||
			(linkGroupTypeId && $LinkGroupStore.firstWhere('type.id', linkGroupTypeId)));
	$: canSaveOrder = linkGroup && !checkIfUncreatedLinks(linkGroup);
	$: firstOrderId = linkGroup && linkGroup.links.data.min('order');
	$: lastOrderId = linkGroup && linkGroup.links.data.max('order');

	function getLinkCategoryId(linkCategory) {
		if (!linkCategory) {
			return;
		}
		for (const linkGroupCategoryName in LinkGroupCategories) {
			if (linkGroupCategoryName.toLowerCase() === linkCategory) {
				return LinkGroupCategories[linkGroupCategoryName];
			}
		}
	}
	function onAddLink(linkGroupId, linkCategoryId) {
		LinkGroupStore.addNewLinkToStore(linkGroupId, linkCategoryId);
	}
	function addLinkAtOrder(linkGroupId, linkCategoryId, order) {
		LinkGroupStore.addNewLinkToStore(linkGroupId, linkCategoryId, order);
	}

	SportStore.getSports(false);

	function grab(event) {
		if (!event.target.closest('.grabber')) {
			return;
		}

		if (event.touches) {
			// modify grabbed element
			grabbed = event.touches[0].target;

			// record offset from cursor to top of element (used for positioning ghost)
			offsetY = event.touches[0].target.closest('.grabber').getBoundingClientRect().y - event.touches[0].clientY;
			mouseYMin = event.touches[0].target.closest('.list').getBoundingClientRect().top;
			mouseYMax = event.touches[0].target.closest('.list').getBoundingClientRect().bottom;

			drag(event.touches[0].clientY);
		} else {
			// modify grabbed element
			grabbed = event.target;

			// record offset from cursor to top of element (used for positioning ghost)
			offsetY = event.target.closest('.grabber').getBoundingClientRect().y - event.clientY;
			mouseYMin = event.target.closest('.list').getBoundingClientRect().top;
			mouseYMax = event.target.closest('.list').getBoundingClientRect().bottom;

			drag(event.clientY);
		}
	}

	// drag handler updates cursor position
	function drag(clientY) {
		if (grabbed) {
			if (clientY <= mouseYMax && clientY >= mouseYMin) {
				mouseY = clientY;
			}
			layerY = ghost.parentNode.getBoundingClientRect().y;
		}
	}

	// touchEnter handler emulates the mouseenter event for touch input
	// (more or less)
	function touchEnter(ev) {
		drag(ev.clientY);
		// trigger dragEnter the first time the cursor moves over a list item
		let target = document.elementFromPoint(ev.clientX, ev.clientY).closest('.item');
		if (target && target != lastTarget) {
			lastTarget = target;
			dragEnter(ev, target);
		}
	}

	function dragEnter(ev, target) {
		// swap items in data
		if (grabbed && target != grabbed && target.classList.contains('item')) {
			changeOrder(parseInt(target.dataset.order));
		}
	}

	function release() {
		grabbed = null;
		grabbedLink = null;
		mouseY = 0;
		offsetY = 0;
		layerY = 0;
		mouseYMax = 0;
		mouseYMin = 0;
	}

	// does the actual moving of items in data
	function changeOrder(to) {
		if (grabbedLink.order !== to && (grabbedLink.order - 1 === to || grabbedLink.order + 1 === to)) {
			LinkGroupStore.changeLinkOrder(linkGroup.id, grabbedLink.order, to);
		}
	}

	function checkIfUncreatedLinks(linkGroup) {
		let uncreatedLinks = false;
		linkGroup.links.data.each((link) => {
			if (link.temp_id) {
				uncreatedLinks = true;
				return false;
			}
		});
		return uncreatedLinks;
	}

	async function saveLinkGroupOrder() {
		let saveOrderOperations = [];

		let counter = 1;
		linkGroup.links.data = linkGroup.links.data
			.where('link_category.id', linkCategoryId)
			.sortBy('order')
			.transform((link) => {
				link.order = counter++;
				return link;
			});
		linkGroup.links.data.each((link) => {
			const promise = new Promise((resolve, reject) => {
				LinkGroupStore.saveLinkGroupOrder(link)
					.then(() => {
						resolve();
					})
					.catch((error) => {
						reject(error);
					});
			});
			saveOrderOperations.push(promise);
		});
		Promise.all(saveOrderOperations)
			.then(() => {
				notify({
					type: 'success',
					title: 'Link group',
					message: 'Link group order updated successfully',
					timeout: 3
				});
				LinkGroupStore.getLinkGroups(linkGroupTypeId);
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Failed to update Link group order',
					timeout: 5
				});
			});
	}
</script>

<Helmet title="Customise menus" />
<svelte:window
	on:mouseup={function (ev) {
		ev.stopPropagation();
		release();
	}}
	on:touchend={function (ev) {
		ev.stopPropagation();
		release();
	}}
/>

<div>
	{#await loading}
		<Loading />
	{:then}
		<div>
			<!-- Page Header -->
			<div class="flex flex-col items-center w-full p-4 mb-4 text-sm bg-white border-b md:py-2">
				<div class="flex items-center justify-between w-full">
					<h1 class="text-xl font-bold">{LinkGroupMap[slug].title}</h1>
					<div class="flex-1 text-center">
						<div class="btn-group">
							{#each $LinkGroupStore.sortBy('order').all() as linkGroupOptions (linkGroupOptions.id)}
								<Link
									href={`/link-groups/${slug}/${linkGroupOptions.id}`}
									class="btn {linkGroupOptions.id === linkGroup?.id ? 'btn-primary-action' : 'btn-muted text-dark'}"
								>
									{linkGroupOptions.name}
								</Link>
							{/each}
						</div>
					</div>
				</div>
			</div>
			<!-- /Page Header -->
			{#if linkGroup}
				<div class="p-2 mx-auto bg-gray-100 rounded max-w-custom">
					<div class="mt-2 rounded">
						<div class="space-y-2">
							<div>
								<div class="flex mb-2">
									<div class="flex flex-row justify-between w-full">
										<div class="pl-1 py-2 font-semibold">{linkGroup.name}</div>
										{#if LinkGroupMap[slug].showCategories}
											<div class="btn-group">
												{#each Object.keys(LinkGroupCategories) as linkGroupCategory}
													<Link
														href={`/link-groups/${slug}/${linkGroup.id}/${linkGroupCategory.toLowerCase()}`}
														class="btn {(
															category
																? linkGroupCategory.toLowerCase() === category
																: linkGroupCategory.toLowerCase() ===
																	LinkGroupCategoryNames[LinkGroupCategories.General].toLowerCase()
														)
															? 'btn-primary-action'
															: 'btn-muted text-dark'}"
													>
														{linkGroupCategory}
													</Link>
												{/each}
											</div>
										{/if}

										<div>
											{#if linkGroup && linkGroup.links.data.count() > 0}
												<button
													on:click={() => saveLinkGroupOrder(linkGroup.id)}
													disabled={!canSaveOrder}
													class="btn btn-primary"
													class:opacity-50={!canSaveOrder}
												>
													Save Links Order
												</button>
											{/if}
											<button
												on:click={() => onAddLink(linkGroup.id, linkCategoryId)}
												class="btn btn-primary-action text-tiny ml-2"
											>
												Add Link
											</button>
										</div>
									</div>
								</div>
								<div class="relative">
									<div
										class="space-y-1 list"
										on:mousemove={function (ev) {
											ev.stopPropagation();
											drag(ev.clientY);
										}}
										on:touchmove={function (ev) {
											ev.stopPropagation();
											drag(ev.touches[0].clientY);
										}}
									>
										<div
											bind:this={ghost}
											id="ghost"
											class="item w-full pr-4"
											class:haunting={grabbed}
											style={'top: calc(' + (mouseY - layerY + offsetY) + 'px - 1px - 0.5rem)'}
										>
											{#if grabbed && grabbedLink !== null}
												{#key grabbedLink.id ?? grabbedLink.temp_id}
													<svelte:component
														this={LinkGroupMap[slug].component}
														link={grabbedLink}
														{linkGroup}
														{linkGroupTypeId}
														{linkCategoryId}
														first={firstOrderId}
														last={lastOrderId}
													/>
												{/key}
											{/if}
										</div>
										{#each linkGroup.links.data
											.where('link_category.id', linkCategoryId)
											.sortBy('order')
											.all() as link (link.id ?? link.temp_id)}
											<div
												class="item"
												data-order={link.order}
												class:opacity-0={grabbed &&
													((grabbedLink.id && link.id && grabbedLink.id === link.id) ||
														(grabbedLink.temp_id && link.temp_id && grabbedLink.temp_id === link.temp_id))}
												on:mousedown={function (ev) {
													grab(ev, this, link);
													grabbedLink = link;
												}}
												on:touchstart={function (ev) {
													grab(ev, this, link);
													grabbedLink = link;
												}}
												on:mouseenter={function (ev) {
													ev.stopPropagation();
													dragEnter(ev, ev.target);
													hoveredLink = link.id ?? link.temp_id;
												}}
												on:mouseleave={function (ev) {
													ev.stopPropagation();
													hoveredLink = undefined;
												}}
												on:touchmove={function (ev) {
													ev.stopPropagation();
													ev.preventDefault();
													touchEnter(ev.touches[0]);
												}}
											>
												<svelte:component
													this={LinkGroupMap[slug].component}
													{link}
													{linkGroup}
													{linkGroupTypeId}
													{linkCategoryId}
													first={firstOrderId}
													last={lastOrderId}
												/>
												{#if slug == 'promotions' && hoveredLink !== undefined && (hoveredLink == link.id || hoveredLink == link.temp_id) && !grabbed}
													<div class="flex flex-row items-center justify-end hover:visible on-btn-hover">
														<button class="btn" on:click={addLinkAtOrder(linkGroup.id, linkCategoryId, link.order + 1)}
															>Create link here</button
														>
													</div>
												{/if}
											</div>
										{/each}
									</div>
								</div>
							</div>
							<div class="py-2 mb-4 border-b" />
						</div>
					</div>
				</div>
			{/if}
		</div>
	{/await}
</div>

<style>
	.item {
		user-select: none;
	}

	#ghost {
		pointer-events: none;
		z-index: -5;
		position: absolute;
		top: 0;
		left: 0;
		opacity: 0;
	}

	#ghost.haunting {
		z-index: 20;
		opacity: 1;
	}
</style>
